import {Group} from 'src/design/atoms/Group';
import {Skeleton} from 'src/design/atoms/Skeleton';
import {Text} from 'src/design/atoms/Text';
import {IconMapPinOff} from '@tabler/icons-react';
import type {CSSProperties, FC} from 'react';
import type {SchematicMapMarker} from 'src/design/organisms/SchematicMap';
import {SchematicMap} from 'src/design/organisms/SchematicMap';
import type {GeoCoordinates} from 'src/shared/utils/geoDistance';
import {
  EARTH_RADIUS_FEET,
  formatDistanceFeet,
  haversineDistanceFeet,
  toRadians,
} from 'src/shared/utils/geoDistance';

export interface SiteLocationMapProps {
  site?: GeoCoordinates;
  user?: GeoCoordinates;
  /** Device-reported GPS accuracy, in feet — rendered as a translucent radius around the site. */
  accuracyFeet?: number;
  /** Renders an animated loading placeholder in place of the grid, ignoring `site`/`user`. */
  capturing?: boolean;
}

// Local equirectangular projection around the midpoint of the two points —
// good enough at site scale, avoids pulling in a real map/tile dependency.
const project = (point: GeoCoordinates, ref: GeoCoordinates) => {
  const refLatRadians = toRadians(ref.latitude);
  const x = toRadians(point.longitude - ref.longitude) * Math.cos(refLatRadians) * EARTH_RADIUS_FEET;
  const y = toRadians(point.latitude - ref.latitude) * EARTH_RADIUS_FEET;
  return {x, y: -y};
};

// px — fixed frame height shared by all three render states (capturing/unavailable/populated),
// short-wide to match the prototype's proportions scaled to the guided flow's ~880px column.
const MAP_FRAME_HEIGHT = 280;

const FRAME_STYLE: CSSProperties = {
  position: 'relative',
  height: MAP_FRAME_HEIGHT,
  width: '100%',
  overflow: 'hidden',
  borderRadius: 8,
  border: '1px solid var(--mantine-color-gray-3)',
  boxSizing: 'border-box',
};

/**
 * Plots the site address and device's captured position on a `SchematicMap`; renders a loading
 * skeleton while capturing, or "Map unavailable" once coordinates are permanently unresolvable.
 */
export const SiteLocationMap: FC<SiteLocationMapProps> = ({
  site,
  user,
  accuracyFeet,
  capturing = false,
}) => {
  if (capturing) {
    return <Skeleton style={FRAME_STYLE} />;
  }

  if (!site || !user) {
    return (
      <div style={FRAME_STYLE}>
        <SchematicMap markers={[]} preserveAspectRatio="none" />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Group gap={6}>
            <IconMapPinOff size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm" c="dimmed">
              Map unavailable
            </Text>
          </Group>
        </div>
      </div>
    );
  }

  const ref: GeoCoordinates = {
    latitude: (site.latitude + user.latitude) / 2,
    longitude: (site.longitude + user.longitude) / 2,
  };
  const sitePoint = project(site, ref);
  const userPoint = project(user, ref);
  const distanceFeet = haversineDistanceFeet(site, user);

  const markers: SchematicMapMarker[] = [];
  if (accuracyFeet) {
    markers.push({id: 'site-accuracy', ...sitePoint, kind: 'radius', r: accuracyFeet});
  }
  markers.push({id: 'site', ...sitePoint, kind: 'point', label: 'Site'});
  markers.push({id: 'user', ...userPoint, kind: 'pin', label: 'You'});

  return (
    <div>
      <div style={FRAME_STYLE}>
        <SchematicMap markers={markers} />
      </div>
      <Text size="xs" c="dimmed" ta="center" mt={4}>
        {formatDistanceFeet(distanceFeet)} from site address
      </Text>
    </div>
  );
};
